import { useEffect, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { NativeSelect } from "@/components/ui/NativeSelect";
import {
  PROJECT_WORKFLOW_STATUS_LABELS,
  PROJECT_WORKFLOW_STATUSES,
  type ProjectWorkflowStatusKey,
} from "@/lib/projects";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

const AUTO_VALUE = "__auto__";

export type ProjectWorkflowStatusChange =
  | { kind: "set"; status: ProjectWorkflowStatusKey }
  | { kind: "auto" };

export function ProjectWorkflowStatusDialog({
  open,
  title,
  description,
  confirmLabel,
  loading,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (begruendung: string) => void;
}) {
  const [begruendung, setBegruendung] = useState("");

  useEffect(() => {
    if (open) setBegruendung("");
  }, [open]);

  const trimmed = begruendung.trim();
  const canSubmit = trimmed.length > 0;

  return (
    <Dialog
      open={open}
      onClose={loading ? () => {} : onClose}
      dirty={trimmed.length > 0}
      size="sm"
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      plainBackdrop
      title={title}
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={loading} />
          <Button
            type="button"
            disabled={!canSubmit}
            loading={loading}
            onClick={() => onConfirm(trimmed)}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm leading-relaxed text-label-secondary">{description}</p>
        <div className="space-y-1">
          <label className="eh-label" htmlFor="po-workflow-status-grund">
            Begründung<span className="ml-1 text-status-red">*</span>
          </label>
          <textarea
            id="po-workflow-status-grund"
            className="eh-input min-h-[5rem]"
            rows={4}
            placeholder="Bitte kurz begründen …"
            value={begruendung}
            disabled={loading}
            onChange={(e) => setBegruendung(e.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}

export function ProjectWorkflowStatusPickerDialog({
  open,
  currentKey,
  currentLabel,
  isManual,
  loading,
  onClose,
  onConfirm,
  onChooseAbgelehnt,
  onChooseCommissionRevoke,
}: {
  open: boolean;
  currentKey: ProjectWorkflowStatusKey;
  currentLabel: string;
  isManual: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: (change: ProjectWorkflowStatusChange, begruendung: string) => void;
  onChooseAbgelehnt?: () => void;
  /** Angeboten/Offen bei bestehender Beauftragung: eigenes Fenster statt dieser Begründung. */
  onChooseCommissionRevoke?: (status: "angeboten" | "offen") => void;
}) {
  const [value, setValue] = useState<string>(currentKey);
  const [begruendung, setBegruendung] = useState("");

  useEffect(() => {
    if (!open) return;
    setValue(currentKey);
    setBegruendung("");
  }, [open, currentKey]);

  const isAuto = value === AUTO_VALUE;
  const nextStatus = isAuto ? null : (value as ProjectWorkflowStatusKey);
  const changed = isAuto || nextStatus !== currentKey;
  const trimmed = begruendung.trim();
  const nextLabel = nextStatus ? PROJECT_WORKFLOW_STATUS_LABELS[nextStatus] : "";
  const description = isAuto
    ? "Der manuelle Status wird aufgehoben. Danach ergibt sich der Status aus Nachweis, Beauftragung und Abrechnung."
    : changed
      ? `Status von „${currentLabel}“ auf „${nextLabel}“ setzen. Die Begründung wird in die Projektnotizen geschrieben.`
      : "Nur nötig, wenn der automatische Status nicht passt. Die Begründung wird in die Projektnotizen geschrieben.";

  return (
    <Dialog
      open={open}
      onClose={loading ? () => {} : onClose}
      dirty={trimmed.length > 0 || changed}
      size="sm"
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      plainBackdrop
      title="Status ändern"
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={loading} />
          <Button
            type="button"
            disabled={!changed || trimmed.length === 0}
            loading={loading}
            onClick={() => {
              if (isAuto) {
                onConfirm({ kind: "auto" }, trimmed);
                return;
              }
              if (!nextStatus) return;
              onConfirm({ kind: "set", status: nextStatus }, trimmed);
            }}
          >
            {isAuto ? "Automatisch ableiten" : "Status speichern"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm leading-relaxed text-label-secondary">{description}</p>
        <div className="space-y-1">
          <label className="eh-label" htmlFor="po-workflow-status-pick">
            Neuer Status
          </label>
          <NativeSelect
            id="po-workflow-status-pick"
            value={value}
            disabled={loading}
            onChange={(event) => {
              const next = event.target.value;
              if (next === "abgelehnt" && currentKey !== "abgelehnt") {
                onChooseAbgelehnt?.();
                return;
              }
              if (
                (next === "angeboten" || next === "offen") &&
                next !== currentKey &&
                onChooseCommissionRevoke
              ) {
                onChooseCommissionRevoke(next);
                return;
              }
              setValue(next);
            }}
          >
            {isManual ? <option value={AUTO_VALUE}>Automatisch ableiten</option> : null}
            {PROJECT_WORKFLOW_STATUSES.map((key) => (
              <option key={key} value={key}>
                {PROJECT_WORKFLOW_STATUS_LABELS[key]}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="space-y-1">
          <label className="eh-label" htmlFor="po-workflow-status-pick-grund">
            Begründung<span className="ml-1 text-status-red">*</span>
          </label>
          <textarea
            id="po-workflow-status-pick-grund"
            className="eh-input min-h-[5rem]"
            rows={4}
            placeholder="Bitte kurz begründen …"
            value={begruendung}
            disabled={loading}
            onChange={(e) => setBegruendung(e.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}
